import { BlocksSnippetAri } from '@atlassian/ari';

import { reportError } from '../reportError.ts';
import type { Snippet, SnippetOwner } from '../snippetTypes.ts';
import { parseSnippetContent } from '../snippetUtils.ts';

import type { SnippetData } from './blockServiceTypes.ts';
import { getCachedOrFetchUserInfos, getDefaultUserInfo } from './userService.ts';

export const transformSnippets = async (snippets: SnippetData[]): Promise<Snippet[]> => {
	const creatorAccountIds = snippets.map((s) => s.createdBy).filter(Boolean);
	const creatorMap = await getCachedOrFetchUserInfos(creatorAccountIds);

	return snippets
		.map((snippet: SnippetData) => transformSnippetData(snippet, creatorMap))
		.filter((snippet: Snippet | undefined) => snippet !== undefined);
};

export const transformSnippet = async (snippet: SnippetData): Promise<Snippet | undefined> => {
	const creatorMap = await getCachedOrFetchUserInfos([snippet.createdBy]);
	return transformSnippetData(snippet, creatorMap);
};

export const transformSnippetData = (
	snippet: SnippetData,
	creatorMap: Map<string, SnippetOwner>,
): Snippet | undefined => {
	try {
		const parsedBody = parseSnippetContent(snippet.body);
		if (parsedBody === undefined) {
			return undefined;
		}

		const createdBy: SnippetOwner =
			creatorMap.get(snippet.createdBy) ?? getDefaultUserInfo(snippet.createdBy, null);

		// BE returns isShared for USER and SITE scope; fallback when missing (e.g. SITE => true).
		const isShared = snippet.isShared ?? false;

		return {
			ari: BlocksSnippetAri.parse(snippet.resourceId),
			snippetId: snippet.snippetId,
			name: snippet.name,
			description: snippet.description,
			bodyFormat: snippet.bodyFormat,
			body: parsedBody,
			product: snippet.product,
			isFavorite: Boolean(snippet.isFavorite),
			status: snippet.status,
			scope: snippet.scope,
			isShared,
			createdBy,
			createdAt: new Date(Number(snippet.createdAt)),
			updatedAt: new Date(Number(snippet.updatedAt)),
			insertCount: snippet.insertCount,
		};
	} catch (error) {
		reportError(
			error instanceof Error
				? error
				: new Error(`Failed to transform snippet ${snippet.snippetId}: ${String(error)}`),
		);
		return undefined;
	}
};
